import React, { useState, useEffect } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import {
  enumOverrideType,
  enumObjClassNames,
  enumObjectTypes,
  kEmployeePositionID,
  objTypeClassMap,
  kDirectSubordinatePositions,
  kSpanOfControl
} from "@coreapp/constants";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { type QChange } from "@coreapp/CoreApp/QChange";
import {
  GetSearchPlaceholder,
  GetSuperIdMovedOut,
  GetSourceValueByField,
  GetSuperIDName,
  checkFieldHasMultipleValues
} from "./RecordEditor.utils";
import { isNullish } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { FindObject } from "./PositionEditorUtils";
import { IsAppendTableContext } from "@coreapp/utils/IsAppendTableContext";

type Props = {
  dbObj: QObjDBObject;
  overrideObj: QObjDBObject;
  type: (typeof enumOverrideType)[keyof typeof enumOverrideType];
  manager: any;
  initChanges: any;
  isPlanEdit: boolean;
  branchAdd?: boolean;
  allowOrphans?: boolean;
  isMultiEdit?: boolean;
  multiDataObjs?: QObjDBObject[];
};

interface RecordEditorState {
  record: QObjDBObject | null;
  baseRecord: QObjDBObject | null;
  recordSupervisor: QObjDBObject | null;
  supervisorPlaceholder: string | null | unknown;
  baseRecordSupervisor: QObjDBObject | null;
  assignedPosition: QObjDBObject | null;
  baseAssignedPosition: QObjDBObject | null;
  override: { state: QObjDBObject | null } | null;
  existingSupervisorID: string | null;
  changeResults: any;
  changeType: (typeof enumOverrideType)[keyof typeof enumOverrideType] | null;
  madeFieldReverts: boolean;
  fetchedRecordFallback: any;
}

export const useRecordEditorController = (props: Props) => {
  const {
    dbObj,
    overrideObj,
    type,
    manager,
    initChanges,
    isMultiEdit = false,
    multiDataObjs
  } = props;

  const getDocument = () => manager?.GetDoc?.() || GetDoc();

  const [recordEditorState, setRecordEditorState] = useState<RecordEditorState>({
    record: dbObj,
    baseRecord: null,
    recordSupervisor: null,
    supervisorPlaceholder: null,
    baseRecordSupervisor: null,
    assignedPosition: null,
    baseAssignedPosition: null,
    override: { state: overrideObj },
    existingSupervisorID: null,
    changeResults: initChanges,
    changeType: type,
    madeFieldReverts: false,
    fetchedRecordFallback: {}
  });

  // Reset state when key props change (e.g., different record being edited)
  useEffect(() => {
    setRecordEditorState({
      record: dbObj,
      baseRecord: null,
      recordSupervisor: null,
      supervisorPlaceholder: null,
      baseRecordSupervisor: null,
      assignedPosition: null,
      baseAssignedPosition: null,
      override: { state: overrideObj },
      existingSupervisorID: null,
      changeResults: initChanges,
      changeType: type,
      madeFieldReverts: false,
      fetchedRecordFallback: {}
    });
  }, [overrideObj]);

  const workingOverride = recordEditorState?.override?.state ?? overrideObj;

  const existingComment = overrideObj ? overrideObj.SmartGetFieldValue("Comment") : null;
  const [comment, setComment] = useState(existingComment);

  const UpdateComment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setComment(value);
  };

  const commentChanged = () => {
    return existingComment !== comment;
  };

  const updateRecordEditorState = (updates: Object) => {
    setRecordEditorState(prevState => ({ ...prevState, ...updates }));
  };

  const Cleanup = () => {
    updateRecordEditorState({});
  };

  const updateRecordSupervisor = (newSupervisor: QObjDBObject | null) => {
    const nextPlaceholder = GetSearchPlaceholder(dbObj, newSupervisor);
    updateRecordEditorState({
      recordSupervisor: newSupervisor,
      supervisorPlaceholder: nextPlaceholder
    });
  };

  const updateRecordSupervisorAsDict = (newSupervisorDict: Object) => {
    const doc = getDocument();
    const objDB = doc?.GetObjDB();
    const className = doc?.IsPositionDocument()
      ? enumObjClassNames.position
      : enumObjClassNames.box;
    const objClass = objDB?.GetClassByName(className);

    let nextDBObject = null;

    if (newSupervisorDict) {
      nextDBObject = new QObjDBObject();
      nextDBObject.InitObject(objClass);
      objClass?.ProcessObject(nextDBObject, newSupervisorDict);
    }

    updateRecordSupervisor(nextDBObject);
  };

  const updateAssignedPosition = (newPosition: QObjDBObject | null) => {
    updateRecordEditorState({
      assignedPosition: newPosition
    });
  };

  const updateAssignedPositionAsDict = (newPositionDict: Object) => {
    const doc = getDocument();
    const objDB = doc?.GetObjDB();
    const objClass = objDB?.GetClassByName(enumObjClassNames.position);

    let nextDBObject = null;

    if (newPositionDict) {
      nextDBObject = new QObjDBObject();
      nextDBObject.InitObject(objClass);
      objClass?.ProcessObject(nextDBObject, newPositionDict);
    }

    updateAssignedPosition(nextDBObject);
  };

  const UpdateFieldValue = (
    theData: { field: string; value: unknown } | { field: string; value: unknown }[]
  ) => {
    const payload = Array.isArray(theData) ? theData : [theData];
    const delta = payload.reduce(
      (acc, item) => {
        acc[item.field] = item.value;
        return acc;
      },
      {} as Record<string, unknown>
    );

    updateRecordEditorState({
      changeResults: { ...recordEditorState.changeResults, ...delta }
    });
  };

  const GetFallbackValueByField = (field: string) => {
    const fetchedRecordFallback = recordEditorState.fetchedRecordFallback;
    if (Object.keys(fetchedRecordFallback).length === 0) return "";

    const fieldValue = fetchedRecordFallback[field];
    if (fieldValue === "" || fieldValue == null) return null;
    return fieldValue;
  };

  const lastActiveChangedField = (
    field: string,
    getPreviousValue: boolean | undefined = undefined,
    getDetails: boolean | undefined = undefined
  ) => {
    return manager.lastActiveChangedField(workingOverride, field, getPreviousValue, getDetails);
  };

  const ResolveSourceValue = (fieldName: string) => {
    const sourceValue = GetSourceValueByField(dbObj, fieldName);
    if (sourceValue !== null) return sourceValue;
    const sourceRecord = dbObj?.fCloneBase;
    const sourceDef = sourceRecord?.GetFieldDefByName?.(fieldName);
    if (sourceDef && sourceRecord?.GetFieldValue(sourceDef, false, true) === "") return "";
    return GetFallbackValueByField(fieldName);
  };

  const GetActiveChangeValueByField = (
    fieldName: string,
    getPreviousValue: boolean | undefined = undefined
  ) => {
    var origVal = ResolveSourceValue(fieldName);

    var lastChangedField = lastActiveChangedField(fieldName, getPreviousValue);
    if (lastChangedField !== null) origVal = lastChangedField.fDelta[fieldName];
    if (origVal == null) origVal = "";
    return origVal;
  };

  const GetCurrentFieldValue = (fieldName: string) => {
    let currentValue = GetActiveChangeValueByField(fieldName);
    const changeResults = recordEditorState.changeResults;
    if (!isNullish(changeResults[fieldName])) currentValue = changeResults[fieldName];

    return currentValue;
  };

  const CheckCoreValueSet = (fieldID: string) => {
    if (!fieldID) return null;

    var doc = getDocument();
    var objDB = doc.GetObjDB();
    var objClass =
      recordEditorState?.record?.InferObjectType() === enumObjectTypes.position
        ? objDB.GetClassByName(enumObjClassNames.position)
        : objDB.GetClassByName(enumObjClassNames.box);
    var fieldDef = objClass.GetFieldDefByID(fieldID);
    var fieldName = fieldDef.GetFieldName();
    var currentValue = GetActiveChangeValueByField(fieldName);
    var activeChangeValue = recordEditorState?.changeResults[fieldName];
    if (!isNullish(activeChangeValue)) currentValue = activeChangeValue;
    return currentValue;
  };

  const unsavedFieldChange = (field: string) => {
    if (!(field in recordEditorState.changeResults)) {
      return false;
    }

    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      return recordEditorState.changeResults[field] !== "";
    }

    return true;
  };

  const handleChartRecordLoadResponse = (
    r: { [x: string]: any[] },
    key: string,
    objType: string
  ) => {
    if (!r[key] || !r[key][0]) return;
    const doc = getDocument();
    const className = objTypeClassMap[objType];
    const objDB = doc?.GetObjDB();
    const objClass = objDB?.GetClassByName(className);
    const newSupervisor = new QObjDBObject();
    newSupervisor.InitObject(objClass);
    objClass?.ProcessObject(newSupervisor, r[key][0]);
    updateRecordSupervisor(newSupervisor);
    return newSupervisor;
  };

  const LoadAssignedPositionDetails = (positionId: string) => {
    if (!positionId) {
      updateAssignedPosition(null);
      return null;
    }

    const doc = getDocument();
    const objDB = doc?.GetObjDB();
    const positionClass = objDB?.GetClassByName(enumObjClassNames.position);
    const positionIDFieldDef = positionClass?.GetRecordIDFieldDef();

    // Try to find the position locally first
    const loadedPosition = positionClass?.FindObject(positionIDFieldDef, positionId, false);

    if (loadedPosition && !IsAppendTableContext(manager)) {
      updateAssignedPosition(loadedPosition);

      // Store base position for reversion
      if (!recordEditorState.baseAssignedPosition) {
        updateRecordEditorState({
          baseAssignedPosition: loadedPosition
        });
      }
      return loadedPosition;
    }

    // If not found locally, load from manager with holders included
    manager?.LoadRecords(
      {
        positionIDs: [positionId],
        includeHolders: true
      },
      (r: { [x: string]: any[] }) => {
        if (!r.positions || !r.positions[0]) return;

        const newPosition = new QObjDBObject();
        newPosition.InitObject(positionClass);
        positionClass?.ProcessObject(newPosition, r.positions[0]);

        // Link holders to the position if any were returned
        if (r.data && r.data.length > 0) {
          const doc = getDocument();
          const objDB = doc?.GetObjDB();
          const boxClass = objDB?.GetClassByName(enumObjClassNames.box);
          const holders = [];

          for (const holderData of r.data) {
            const holderPositionId = holderData[kEmployeePositionID];
            if (holderPositionId === positionId) {
              const holder = new QObjDBObject();
              holder.InitObject(boxClass);
              boxClass?.ProcessObject(holder, holderData);

              // Apply overrides to the holder
              const override = manager?.fOverrideClass?.GetExistingBoxOverride(holder);
              if (override) {
                override.ApplyChangesReadOnly(holder);
              }

              holders.push(holder);
            }
          }

          if (holders.length > 0) {
            newPosition.SetHolderList(holders);
          }
        }
        updateAssignedPosition(newPosition);

        // Store base position for reversion
        if (!recordEditorState.baseAssignedPosition) {
          updateRecordEditorState({
            baseAssignedPosition: newPosition
          });
        }
        return newPosition;
      }
    );
  };

  // superIDOverride lets a caller that already knows the supervisor it wants (the field-revert
  // paths) bypass the mount-time precedence chain below, which prefers initChanges and would
  // otherwise reload the pre-revert supervisor.
  const LoadSupervisorDetails = (targetOverride: QObjDBObject, superIDOverride?: string) => {
    const theOverride = targetOverride ?? recordEditorState.override?.state;
    const doc = getDocument();
    const hierarchyObjectType = doc?.GetHierarchyObjectType();
    const objType = dbObj?.InferObjectType();
    const isCrossLookup =
      objType === enumObjectTypes.holder && hierarchyObjectType === enumObjectTypes.position;
    const classDef = isCrossLookup ? doc?.GetPositionClass() : dbObj.GetParentClass();
    const superKeyDef = classDef?.GetSupervisorIDFieldDef();

    const superKey = superKeyDef?.GetFieldName();
    const personIDDef = classDef?.GetRecordIDFieldDef();
    const movedOutSuperId = GetSuperIdMovedOut(dbObj, theOverride);

    // Annotated any: the coreapp accessors below are untyped JS and return unknown.
    let superID: any = superIDOverride ?? null;

    if (!superID) {
      if (isCrossLookup) {
        const parentPosition = dbObj.GetParentPosition();
        if (parentPosition) {
          superID = parentPosition.GetObjectSupervisorID();
        }
      } else {
        superID = initChanges[superKey] ?? movedOutSuperId ?? GetActiveChangeValueByField(superKey);
      }
    }

    if (!superID) {
      return;
    }

    // Find original supervisor for reversion (needed for tooltips). When a clone base
    // exists it is authoritative even if the supervisor was blank there, so don't let a
    // falsy base value fall through to the post-delta (current) supervisor.
    let baseSuperId = null;
    if (superKeyDef) {
      baseSuperId = dbObj?.fCloneBase
        ? dbObj.fCloneBase.GetFieldValue(superKeyDef, false, true)
        : dbObj?.GetFieldValue(superKeyDef);
    }
    const baseSuper = baseSuperId ? classDef?.FindObject(personIDDef, baseSuperId, false) : null;

    // FindObject only sees records loaded into the chart, which the base supervisor of an
    // append-table override usually is not. Substituting the current supervisor there made
    // the revert tooltip name the wrong person, so only reuse it when it IS the base.
    const baseMatchesCurrent = String(baseSuperId ?? "") === String(superID);
    const resolveBaseSupervisor = (current: any) =>
      baseSuper || (baseMatchesCurrent ? current : null);

    const loadedRecord = classDef?.FindObject(personIDDef, superID, false);
    if (loadedRecord) {
      updateRecordSupervisor(loadedRecord);

      // Always update base supervisor info for reversion, whether in plan edit or not
      updateRecordEditorState({
        baseRecordSupervisor: resolveBaseSupervisor(loadedRecord)
      });
      return loadedRecord;
    }

    if (movedOutSuperId) {
      manager?.LoadOutsidePlanRecords(
        [superID],
        (r: { [x: string]: any[] }) => {
          const supervisor = handleChartRecordLoadResponse(r, "data", objType);
          // Also set base supervisor for reversion info
          updateRecordEditorState({
            baseRecordSupervisor: resolveBaseSupervisor(supervisor)
          });
          return supervisor;
        },
        dbObj?.IsPositionObj()
      );
    } else {
      let type = objType || enumObjectTypes.holder;
      if (isCrossLookup) {
        type = enumObjectTypes.position;
      }

      let searchKey = type === enumObjectTypes.position ? "positionIDs" : "recordIDs";
      let searchQuery = {} as any;
      searchQuery[searchKey] = [superID];

      manager?.LoadRecords(searchQuery, (r: { [x: string]: any[] }) => {
        const key = type === enumObjectTypes.position ? "positions" : "data";
        const supervisor = handleChartRecordLoadResponse(r, key, type);
        // Also set base supervisor for reversion info
        updateRecordEditorState({
          baseRecordSupervisor: resolveBaseSupervisor(supervisor)
        });
        return supervisor;
      });
    }
  };

  const InitializeTarget = (
    initOverride: QObjDBObject,
    baseRec: QObjDBObject,
    loadRecord: boolean | undefined = true
  ) => {
    Cleanup();
    updateRecordEditorState({
      override: { state: initOverride },
      record: baseRec,
      baseRecord: baseRec.fCloneBase || baseRec,
      changeType: type,
      changeResults: initChanges
    });
    if (loadRecord) LoadSupervisorDetails(initOverride);
  };

  const GetExistingFieldChanges = (fieldName: string) => {
    const isAdd = IsAddOverride();
    // Also exclude cases like talent-pool with placement entry ahead of the add override
    const overrideStopIndex = isAdd ? GetOverrideStopIndex() : -1;
    return workingOverride?.fChanges?.filter((item: QChange, index: number) => {
      if (isAdd && index <= overrideStopIndex) return false;
      if (isNullish(item.fDelta)) return false;
      return Object.keys(item.fDelta).includes(fieldName);
    });
  };

  const ValidateBasicFields = (fieldsList: string[]) => {
    const dbObj = recordEditorState?.record;
    let failureArr: string[] = [];
    fieldsList.map(fieldID => {
      let fieldDef = dbObj?.GetFieldDefByID(fieldID) || dbObj?.GetFieldDefByName(fieldID);
      if (!fieldDef) return;
      let fieldName = fieldDef.GetFieldName();
      let currentValue = CheckCoreValueSet(fieldID);
      if (currentValue == null || currentValue === "") {
        failureArr.push(fieldName);
      }
    });

    return failureArr;
  };

  const ValidateAdd = (
    recordIDKey: string,
    superIDKey: string,
    allowOrphans: boolean | undefined = false,
    changesResults: any = null
  ) => {
    let failureArr: string[] = [];

    const dbObj = recordEditorState?.record;
    if (!dbObj) return failureArr;

    const doc = getDocument();
    const objType = dbObj?.InferObjectType();
    const hierarchyObjectType = doc?.GetHierarchyObjectType();

    // Use passed changesResults or fall back to controller state
    if (!changesResults) {
      changesResults = recordEditorState?.changeResults;
    }

    const recordID = CheckCoreValueSet(recordIDKey);
    const superID = CheckCoreValueSet(superIDKey);
    if (superIDKey && !superID) {
      if (allowOrphans || objType !== hierarchyObjectType) {
        changesResults[GetSuperIDName(dbObj)] = "";
      } else {
        failureArr.push(appstr("Reports to"));
      }
    }

    const existingOverrideID = workingOverride?.SmartGetFieldValue("override_id");
    if (existingOverrideID == null) {
      if (recordID === "" || recordID == null) failureArr.push(appstr(recordIDKey));
    }

    return failureArr;
  };

  const ValidateMoveOrMoveOut = (
    allowOrphans: boolean | undefined = false,
    isCommentOnlyChange: boolean
  ) => {
    const dbObj = recordEditorState?.record;

    let failureArr: string[] = [];
    if (!dbObj) return failureArr;

    const changesResults = recordEditorState?.changeResults;
    const existingSupervisorID = recordEditorState?.existingSupervisorID;
    const doc = getDocument();
    const objType = dbObj?.InferObjectType();
    const hierarchyObjectType = doc?.GetHierarchyObjectType();

    let superIDName = GetSuperIDName(dbObj);
    if (hierarchyObjectType === enumObjectTypes.position && objType === enumObjectTypes.holder) {
      const classDef = doc?.GetBoxClass() ?? dbObj.GetParentClass();
      superIDName = classDef?.GetFieldDefByID(kEmployeePositionID)?.GetFieldName() ?? superIDName;
    }

    const newSupervisorID = changesResults?.[superIDName] ?? "";
    if ((workingOverride?.fChanges?.length ?? 0) < 1 || isCommentOnlyChange) {
      if (
        (newSupervisorID.length === 0 && !allowOrphans) ||
        changesResults[superIDName] === existingSupervisorID
      )
        failureArr.push(appstr("New SupervisorID Required"));
    }

    return failureArr;
  };

  const ValidateChangesExist = (
    changes: Array<Object>,
    isCommentOnlyChange: boolean,
    additionalData: any
  ) => {
    let failureArr = [];
    const workingChanges = workingOverride?.fChanges;
    let changeCount = workingChanges?.length ?? 0;
    if (additionalData?.assignmentChanges) {
      changeCount += 1;
    }
    if (
      (changeCount < 1 || isCommentOnlyChange) &&
      Object.keys(changes).length === 0 &&
      !recordEditorState.madeFieldReverts
    ) {
      failureArr.push(appstr("No Changed Fields"));
    }

    return failureArr;
  };

  const CheckDoRollup = (
    onSaveCallback: Function,
    additionalData: any = {},
    validate = true,
    skipValidationOverride = false,
    additionalChanges: any = {}
  ) => {
    let overrideType = GetChangeType();
    let isNewOverride = workingOverride.SmartGetFieldValue("override_id") == null;
    const subField = dbObj.IsPositionObj?.() ? kDirectSubordinatePositions : kSpanOfControl;
    const spanOfControl = (dbObj.SmartGetFieldValue(subField) as number) || 0;

    let applicableType = [
      enumOverrideType.delete,
      enumOverrideType.moveout,
      enumOverrideType.move
    ].includes(overrideType);

    const shouldPromptForRollup = overrideType === enumOverrideType.delete || isNewOverride;

    let existsAndHasSubs = dbObj && spanOfControl > 0;
    if (applicableType && shouldPromptForRollup && existsAndHasSubs) {
      let headerLabel = "";
      let altLabel;
      switch (overrideType) {
        case enumOverrideType.move:
          altLabel = appstr("Move With Manager");
          headerLabel = appstr("Move Manager");
          break;
        case enumOverrideType.moveout:
          altLabel = appstr("Orphan");
          headerLabel = appstr("Move Out Manager");
          break;
        case enumOverrideType.delete:
          altLabel = appstr("Disconnect");
          headerLabel = appstr("Delete Manager");
          break;
      }

      const wsMgr = GetWSMgr();
      wsMgr?.CreateReactAlert(
        headerLabel,
        appstr("What would you like to do with the subordinates of this manager?"),
        () => {
          SaveRecordChanges(
            onSaveCallback,
            additionalData,
            false,
            skipValidationOverride,
            additionalChanges
          );
        },
        () => {
          workingOverride.fRollUp = true;
          updateRecordEditorState({ override: { state: workingOverride } });
          SaveRecordChanges(
            onSaveCallback,
            additionalData,
            false,
            skipValidationOverride,
            additionalChanges
          );
        },
        altLabel,
        appstr("Roll Up")
      );

      return true;
    }
    return false;
  };

  // Helper function for common save routines
  const SaveRecordChanges = async (
    onSaveCallback: Function,
    additionalData: any = {},
    validate = true,
    skipValidationOverride = false,
    additionalChanges: any = {}
  ) => {
    let filteredChangeResults = recordEditorState.changeResults;
    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      filteredChangeResults = Object.keys(recordEditorState.changeResults).reduce(
        (filtered, field) => {
          const value = recordEditorState.changeResults[field];
          filtered[field] = value;
          if (
            checkFieldHasMultipleValues(field, multiDataObjs) &&
            (value === "" || value == null)
          ) {
            delete filtered[field];
          }
          return filtered;
        },
        {} as any
      );
    }

    const parentPositionFieldName = recordEditorState.record
      ?.GetParentClass?.()
      ?.GetSupervisorIDFieldDef?.()
      ?.GetFieldName();

    const mergedChangeResults = {
      ...filteredChangeResults,
      ...additionalChanges
    };

    if (parentPositionFieldName && parentPositionFieldName in mergedChangeResults) {
      const parentPositionValue = mergedChangeResults[parentPositionFieldName];
      mergedChangeResults.Orphan =
        parentPositionValue === "" || parentPositionValue == null ? 1 : 0;
    }

    let idKey = recordEditorState?.record?.GetRecordIDFieldDef()?.GetFieldID();

    if (validate && !skipValidationOverride) {
      // Run validation
      const changeTypeValue = GetChangeType();
      const validCommit = ValidCommit(mergedChangeResults, changeTypeValue, additionalData);
      if (validCommit !== true) {
        // Handle validation failure
        return {
          success: false,
          errors: validCommit
        };
      }

      if (
        CheckDoRollup(
          onSaveCallback,
          additionalData,
          validate,
          skipValidationOverride,
          additionalChanges
        )
      )
        return { success: false };

      const validOverride = manager.IsValidOverride?.(
        mergedChangeResults,
        workingOverride,
        CheckCoreValueSet(idKey)
      );

      if (!validOverride) return { success: false };
    }

    if (type === enumOverrideType.add) {
      workingOverride?.SmartSetFieldValue("RefID", CheckCoreValueSet(idKey));
    }

    let result = {
      changes: mergedChangeResults,
      onlyComment: IsOnlyCommentChange(),
      revertAll: false,
      branchAdd: props.branchAdd,
      madeFieldReverts: recordEditorState.madeFieldReverts,
      comment: comment,
      ...additionalData
    };

    const callbackResult = await onSaveCallback({
      result,
      override: workingOverride,
      changeType: GetChangeType(additionalData?.changeType)
    });

    if (callbackResult?.success === false) {
      return { success: false };
    }

    return { success: true };
  };

  // Get the change type from the override
  const GetChangeType = (changeType?: any) => {
    return changeType ?? type ?? workingOverride?.SmartGetFieldValue("Type");
  };

  const IsAddOverride = () =>
    workingOverride?.SmartGetFieldValue?.("Type") === enumOverrideType.add;

  const GetOverrideStopIndex = () => {
    if (!IsAddOverride()) return 0;

    const stopIndex =
      workingOverride?.fChanges?.findIndex(
        (change: QChange) => change.fChangeType === enumOverrideType.add
      ) ?? -1;

    return stopIndex >= 0 ? stopIndex : 0;
  };

  const GetBaseFieldValue = (fieldDef: any) => {
    if (IsAddOverride()) {
      return workingOverride?.fChanges?.[GetOverrideStopIndex()]?.fDelta?.[
        fieldDef?.GetFieldName?.()
      ];
    }
    // Shares ResolveSourceValue with the value path so the revert tooltip always names the
    // value a revert will actually land on.
    const fieldName = fieldDef?.GetFieldName?.();
    if (!fieldName) return null;
    return ResolveSourceValue(fieldName);
  };

  // Check if this is a comment-only change
  const IsOnlyCommentChange = () => {
    let newChangesExist = Object.keys(recordEditorState.changeResults).length !== 0;
    let commentChanged = comment !== existingComment;
    let isNewOverride = !workingOverride?.SmartGetFieldValue("override_id");
    let commentOverride = CommentOnlyChange();
    return !isNewOverride && !commentOverride && commentChanged && !newChangesExist;
  };

  // Main validation routine used by all modals
  const ValidCommit = (changes: any, changeTypeValue: any, additionalData: any) => {
    const isCommentOnlyChange = CommentOnlyChange();
    const objectType = overrideObj?.SmartGetFieldValue("objType");
    const doc = getDocument();
    const isPositionObj = objectType === enumObjectTypes.position;
    const classDef = isPositionObj ? doc?.GetPositionClass() : doc?.GetBoxClass();
    const idField = classDef?.GetRecordIDFieldDef();
    const parentIDField = classDef?.GetSupervisorIDFieldDef();

    const recordIDKey = idField?.GetFieldID();
    const parentRecordIDKey = parentIDField?.GetFieldID();
    const titleKey = classDef?.GetTitleFieldDef()?.GetFieldID() ?? null;

    let minRequiredFields: string[] = [];

    // Only check minimum required fields for 'add' operations
    if (changeTypeValue === enumOverrideType.add) {
      minRequiredFields = [titleKey];
      if (!isPositionObj) {
        minRequiredFields = ["Name", titleKey];
      } else if (!props.allowOrphans) {
        minRequiredFields.push(parentRecordIDKey);
      }
    }

    let failureArr: string[] = ValidateBasicFields(minRequiredFields);
    if (changeTypeValue === enumOverrideType.add) {
      failureArr = [
        ...failureArr,
        ...ValidateAdd(recordIDKey, parentRecordIDKey, props.allowOrphans, changes)
      ];
    } else if (
      changeTypeValue === enumOverrideType.move ||
      changeTypeValue === enumOverrideType.moveout
    ) {
      failureArr = [
        ...failureArr,
        ...ValidateMoveOrMoveOut(props.allowOrphans, isCommentOnlyChange)
      ];
    } else if (changeTypeValue === enumOverrideType.change) {
      failureArr = [
        ...failureArr,
        ...ValidateChangesExist(changes, isCommentOnlyChange, additionalData)
      ];
    }

    if (changeTypeValue !== enumOverrideType.add && Object.keys(changes).includes(recordIDKey))
      return false;

    if (failureArr.length > 0) return failureArr;

    return true;
  };

  // Helper function to check for comment-only changes
  const CommentOnlyChange = () => {
    return GetChangeType() === enumOverrideType.comment && workingOverride?.fChanges?.length == 1;
  };

  // Helper function to merge additional changes with current change results
  const MergeChanges = (additionalChanges: any = {}) => {
    return {
      ...recordEditorState.changeResults,
      ...additionalChanges
    };
  };

  // State setters for manual state updates (needed for field reversion)
  const setOverride = (overrideData: { state: QObjDBObject }) => {
    updateRecordEditorState({ override: overrideData });
  };

  const setMadeFieldReverts = (value: boolean) => {
    updateRecordEditorState({ madeFieldReverts: value });
  };

  // Business logic functions moved from BaseRecordEditorModal
  const UnsavedChanges = () => {
    const hasDelta = Object.keys(recordEditorState.changeResults).length > 0;
    const deltaChanged =
      JSON.stringify(initChanges ?? {}) !== JSON.stringify(recordEditorState.changeResults ?? {});
    return (hasDelta && deltaChanged) || recordEditorState.madeFieldReverts;
  };

  const changeTarget = (
    callbackFn: Function,
    newOverrideObj: QObjDBObject,
    newDbObj: QObjDBObject,
    loadRecord: boolean = true
  ) => {
    // Check if we need to save
    const unsavedChanges = UnsavedChanges();
    if (!unsavedChanges) {
      // Proceed to change target
      InitializeTarget(newOverrideObj, newDbObj, loadRecord);
      return;
    }
    // Return that we need to save
    return { needsSave: true, callbackFn };
  };

  const DoRevertAll = () => {
    const changeList = workingOverride?.fChanges ?? [];
    for (let i = changeList.length; i > 0; i--) {
      const theChange = changeList[i - 1];
      if (theChange.IsReverted() !== true) {
        theChange.fIsReverted = true;
      }
    }
    manager?.SendUpdate(workingOverride, false, false, true);
  };

  const GetHolder = (employeeData: any): QObjDBObject => {
    const doc = getDocument();
    const objDB = doc?.GetObjDB();
    const boxClass = objDB?.GetClassByName("Box");
    const personIDDef = boxClass?.GetRecordIDFieldDef();
    const idKey = personIDDef?.GetFieldName();

    if (employeeData instanceof QObjDBObject) return employeeData;

    let holderDBObj = FindObject(employeeData[idKey], enumObjClassNames.box);
    if (!holderDBObj) {
      holderDBObj = boxClass?.AddNewObject();
    }
    boxClass?.ProcessObject(holderDBObj, employeeData);
    return holderDBObj;
  };

  const processAssignmentChanges = (currentAssignees: any, originalAssignees?: any[]) => {
    // Get original holders for comparison
    const originalHolders = originalAssignees || dbObj.GetHolderList() || [];
    const originalHolderIds = originalHolders.map(holder =>
      holder.SmartGetFieldValue("EmployeeID")
    );

    // Current assignments in state
    const currentAssignmentIds = currentAssignees.map(
      (emp: { SmartGetFieldValue: (arg0: string) => any }) => emp.SmartGetFieldValue("EmployeeID")
    );

    // Find added assignments (employees that are in current but not original)
    const addedAssignments = currentAssignees.filter(
      (emp: { SmartGetFieldValue: (arg0: string) => unknown }) =>
        !originalHolderIds.includes(emp.SmartGetFieldValue("EmployeeID"))
    );

    // Find removed assignments (employees that are in original but not current)
    const removedAssignments = originalHolders.filter(
      holder => !currentAssignmentIds.includes(holder.SmartGetFieldValue("EmployeeID"))
    );

    return {
      added: addedAssignments,
      removed: removedAssignments
    };
  };

  return {
    record: recordEditorState.record,
    baseRecord: recordEditorState.baseRecord,
    recordSupervisor: recordEditorState.recordSupervisor,
    supervisorPlaceholder: recordEditorState.supervisorPlaceholder,
    baseRecordSupervisor: recordEditorState.baseRecordSupervisor,
    assignedPosition: recordEditorState.assignedPosition,
    baseAssignedPosition: recordEditorState.baseAssignedPosition,
    override: recordEditorState.override,
    existingSupervisorID: recordEditorState.existingSupervisorID,
    changeResults: recordEditorState.changeResults,
    changeType: recordEditorState.changeType,
    madeFieldReverts: recordEditorState.madeFieldReverts,
    fetchedRecordFallback: recordEditorState.fetchedRecordFallback,
    workingOverride,
    updateRecordEditorState,
    Cleanup,
    updateRecordSupervisor,
    updateAssignedPosition,
    updateAssignedPositionAsDict,
    LoadAssignedPositionDetails,
    InitializeTarget,
    LoadSupervisorDetails,
    GetActiveChangeValueByField,
    lastActiveChangedField,
    GetFallbackValueByField,
    GetCurrentFieldValue,
    updateRecordSupervisorAsDict,
    unsavedFieldChange,
    GetExistingFieldChanges,
    UpdateFieldValue,
    CheckCoreValueSet,
    ValidateBasicFields,
    ValidateChangesExist,
    ValidateAdd,
    ValidateMoveOrMoveOut,
    SaveRecordChanges,
    GetChangeType,
    IsAddOverride,
    GetOverrideStopIndex,
    GetBaseFieldValue,
    ValidCommit,
    comment,
    UpdateComment,
    commentChanged,
    IsOnlyCommentChange,
    CommentOnlyChange,
    MergeChanges,
    setOverride,
    setMadeFieldReverts,
    UnsavedChanges,
    changeTarget,
    DoRevertAll,
    processAssignmentChanges,
    GetHolder
  };
};
